import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../utils/api';

export const fetchVolunteers = createAsyncThunk(
  'volunteerAssignment/fetchVolunteers',
  async ({ page = 1, pageSize = 10 }) => {
    const response = await api.get('/volunteer-assignment/volunteers', { params: { page, pageSize } });
    return response.data.data;
  }
);

export const fetchVolunteerIssues = createAsyncThunk(
  'volunteerAssignment/fetchVolunteerIssues',
  async (volunteerId) => {
    const response = await api.get(`/volunteer-assignment/volunteers/${volunteerId}/issues`);
    return response.data.data;
  }
);

export const fetchVolunteerMessages = createAsyncThunk(
  'volunteerAssignment/fetchVolunteerMessages',
  async ({ volunteerId, representativeId }) => {
    const response = await api.get(`/volunteer-assignment/volunteers/${volunteerId}/messages`, {
      params: { representativeId },
    });
    return response.data.data;
  }
);

export const sendVolunteerMessage = createAsyncThunk(
  'volunteerAssignment/sendVolunteerMessage',
  async ({ volunteerId, representativeId, content }) => {
    const response = await api.post(`/volunteer-assignment/volunteers/${volunteerId}/messages`, {
      representativeId,
      content,
    });
    return response.data.data;
  }
);

export const createVolunteer = createAsyncThunk(
  'volunteerAssignment/createVolunteer',
  async (volunteerData) => {
    const response = await api.post('/volunteer-assignment/volunteers', volunteerData);
    return response.data.data;
  }
);

export const updateVolunteer = createAsyncThunk(
  'volunteerAssignment/updateVolunteer',
  async ({ id, data }) => {
    const response = await api.put(`/volunteer-assignment/volunteers/${id}`, data);
    return response.data.data;
  }
);

export const deleteVolunteer = createAsyncThunk(
  'volunteerAssignment/deleteVolunteer',
  async (id) => {
    await api.delete(`/volunteer-assignment/volunteers/${id}`);
    return id;
  }
);

export const assignVolunteer = createAsyncThunk(
  'volunteerAssignment/assignVolunteer',
  async ({ id, area, rotationSchedule, status }) => {
    const response = await api.post(`/volunteer-assignment/volunteers/${id}/assign`, {
      area,
      rotationSchedule,
      status,
    });
    return response.data.data;
  }
);

const initialState = {
  volunteers: [],
  totalCount: 0,
  page: 1,
  pageSize: 10,
  issues: [],
  messages: [],
  status: 'idle',
  error: null,
};

const volunteerAssignmentSlice = createSlice({
  name: 'volunteerAssignment',
  initialState,
  reducers: {
    clearMessages(state) {
      state.messages = [];
    },
    clearIssues(state) {
      state.issues = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchVolunteers.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchVolunteers.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.volunteers = action.payload.volunteers;
        state.totalCount = action.payload.totalCount;
        state.page = action.payload.page;
        state.pageSize = action.payload.pageSize;
      })
      .addCase(fetchVolunteers.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message ?? null;
      })
      .addCase(fetchVolunteerIssues.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchVolunteerIssues.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.issues = action.payload;
      })
      .addCase(fetchVolunteerIssues.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message ?? null;
      })
      .addCase(fetchVolunteerMessages.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchVolunteerMessages.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.messages = action.payload;
      })
      .addCase(fetchVolunteerMessages.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message ?? null;
      })
      .addCase(sendVolunteerMessage.fulfilled, (state, action) => {
        state.messages.push(action.payload);
      })
      .addCase(createVolunteer.fulfilled, (state, action) => {
        state.volunteers.unshift(action.payload);
        state.totalCount += 1;
      })
      .addCase(updateVolunteer.fulfilled, (state, action) => {
        const index = state.volunteers.findIndex((v) => v.id === action.payload.id);
        if (index !== -1) {
          state.volunteers[index] = action.payload;
        }
      })
      .addCase(deleteVolunteer.fulfilled, (state, action) => {
        state.volunteers = state.volunteers.filter((v) => v.id !== action.payload);
        state.totalCount -= 1;
      })
      .addCase(assignVolunteer.fulfilled, (state, action) => {
        const index = state.volunteers.findIndex((v) => v.id === action.payload.id);
        if (index !== -1) {
          state.volunteers[index] = action.payload;
        }
      });
  },
});

export const { clearMessages, clearIssues } = volunteerAssignmentSlice.actions;

export default volunteerAssignmentSlice.reducer;
